import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Search, MapPin, Building2, Users, Star, Crown, Clock, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { AuthContext } from "@/App";
import { VerifiedBadge } from "@/components/VerifiedBadge";

export default function Marketplace() {
  const { user } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState("listings");
  const [listings, setListings] = useState([]);
  const [requirements, setRequirements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    industry: "all",
    location: "",
    min_price: "",
    max_price: ""
  });

  const industries = ["Technology", "Retail", "Manufacturing", "Healthcare", "Food & Beverage", "Real Estate", "Services", "Other"];

  useEffect(() => {
    fetchListings();
    fetchRequirements();
  }, []);

  const fetchListings = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.industry && filters.industry !== "all") params.append("industry", filters.industry);
      if (filters.location) params.append("location", filters.location);
      if (filters.min_price) params.append("min_price", filters.min_price);
      if (filters.max_price) params.append("max_price", filters.max_price);
      
      const { data } = await api.get(`/listings?${params.toString()}`);
      setListings(data);
    } catch (error) {
      toast.error("Failed to fetch listings");
    } finally {
      setLoading(false);
    }
  };

  const fetchRequirements = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.industry && filters.industry !== "all") params.append("industry", filters.industry);
      
      const { data } = await api.get(`/requirements?${params.toString()}`);
      setRequirements(data);
    } catch (error) {
      console.error("Failed to fetch requirements:", error);
    }
  };

  const handleSearch = () => {
    fetchListings();
    fetchRequirements();
  };

  const getTierIcon = (tier) => {
    switch(tier) {
      case 'concierge': return <Crown className="w-4 h-4 text-purple-600" />;
      case 'premium': return <Star className="w-4 h-4 text-blue-600" />;
      default: return null;
    }
  };

  const getTierBadge = (tier) => {
    switch(tier) {
      case 'concierge': return <Badge className="bg-purple-600">Concierge</Badge>;
      case 'premium': return <Badge className="bg-blue-600">Premium</Badge>;
      default: return <Badge variant="outline">Free</Badge>;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-12" data-testid="marketplace-header">
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">Business Marketplace</h1>
          <p className="text-lg text-slate-600">Discover verified businesses for sale</p>
        </div>

        {/* Filters */}
        <Card className="mb-8" data-testid="filter-card">
          <CardContent className="pt-6">
            <div className="grid md:grid-cols-4 gap-4">
              <Select value={filters.industry} onValueChange={(value) => setFilters({ ...filters, industry: value })}>
                <SelectTrigger data-testid="industry-filter">
                  <SelectValue placeholder="Industry" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Industries</SelectItem>
                  {industries.map(ind => (
                    <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Input
                placeholder="Location"
                value={filters.location}
                onChange={(e) => setFilters({ ...filters, location: e.target.value })}
                data-testid="location-filter"
              />

              <Input
                type="number"
                placeholder="Min Price"
                value={filters.min_price}
                onChange={(e) => setFilters({ ...filters, min_price: e.target.value })}
                data-testid="min-price-filter"
              />

              <Input
                type="number"
                placeholder="Max Price"
                value={filters.max_price}
                onChange={(e) => setFilters({ ...filters, max_price: e.target.value })}
                data-testid="max-price-filter"
              />
            </div>
            <Button onClick={handleSearch} className="mt-4 bg-blue-600 hover:bg-blue-700" data-testid="search-button">
              <Search className="w-4 h-4 mr-2" />
              Search
            </Button>
          </CardContent>
        </Card>

        {/* Tabs for Listings and Requirements */}
        <Tabs defaultValue="listings" className="w-full" onValueChange={setActiveTab}>
          <TabsList className="mb-6 bg-slate-100">
            <TabsTrigger value="listings" className="data-[state=active]:bg-white" data-testid="tab-listings">
              <Building2 className="w-4 h-4 mr-2" />
              Businesses for Sale ({listings.length})
            </TabsTrigger>
            <TabsTrigger value="requirements" className="data-[state=active]:bg-white" data-testid="tab-requirements">
              <Users className="w-4 h-4 mr-2" />
              Buyer Requirements ({requirements.length})
            </TabsTrigger>
          </TabsList>

          {/* Businesses for Sale Tab */}
          <TabsContent value="listings">
            {loading ? (
              <div className="text-center py-12">Loading...</div>
            ) : listings.length === 0 ? (
              <div className="text-center py-12" data-testid="no-listings">
                <Building2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600">No listings found</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="listings-grid">
                {listings.map((listing) => (
                  <Link to={`/listing/${listing.id}`} key={listing.id}>
                    <Card className="group relative bg-white border border-slate-200 rounded-xl overflow-hidden hover:shadow-xl hover:shadow-slate-200/50 transition-all duration-300 h-full" data-testid={`listing-card-${listing.id}`}>
                      <div className="aspect-video bg-slate-100 overflow-hidden">
                        {listing.images && listing.images.length > 0 ? (
                          <img
                            src={listing.images[0]}
                            alt={listing.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Building2 className="w-12 h-12 text-slate-300" />
                          </div>
                        )}
                      </div>
                      <CardHeader>
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <CardTitle className="text-xl">{listing.name}</CardTitle>
                            {listing.seller_verified && <VerifiedBadge size="sm" />}
                          </div>
                          {listing.verified && (
                            <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full font-medium">Verified</span>
                          )}
                        </div>
                        <CardDescription className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {listing.location}
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          <p className="text-sm text-slate-600">{listing.industry}</p>
                          <p className="text-2xl font-bold text-slate-900">{formatCurrency(listing.asking_price)}</p>
                          <div className="flex gap-4 text-sm text-slate-600 pt-2 border-t border-slate-100">
                            <div>
                              <span className="font-medium">Revenue:</span> {formatCurrency(listing.revenue)}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Buyer Requirements Tab */}
          <TabsContent value="requirements">
            {requirements.length === 0 ? (
              <div className="text-center py-12" data-testid="no-requirements">
                <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600">No buyer requirements found</p>
                <p className="text-sm text-slate-500 mt-2">Buyers post their requirements here when looking for specific businesses</p>
              </div>
            ) : (
              <div className="space-y-6" data-testid="requirements-grid">
                {/* Featured Requirements first */}
                {requirements.filter(r => r.featured).length > 0 && (
                  <div className="mb-8">
                    <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <Star className="w-5 h-5 text-amber-500" />
                      Featured Requirements
                    </h3>
                    <div className="grid md:grid-cols-2 gap-6">
                      {requirements.filter(r => r.featured).map((req) => (
                        <Card key={req.id} className="border-2 border-amber-200 bg-amber-50/30 hover:shadow-lg transition-all" data-testid={`requirement-card-${req.id}`}>
                          <CardHeader>
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  {getTierIcon(req.tier)}
                                  <CardTitle className="text-lg">{req.title}</CardTitle>
                                </div>
                                <CardDescription className="flex items-center gap-1">
                                  <MapPin className="w-3 h-3" />
                                  {req.location}
                                </CardDescription>
                              </div>
                              {getTierBadge(req.tier)}
                            </div>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-3">
                              <p className="text-sm text-slate-600 line-clamp-2">{req.description}</p>
                              <div className="flex flex-wrap gap-2">
                                <Badge variant="secondary">{req.industry}</Badge>
                                <Badge variant="secondary">{req.business_type}</Badge>
                              </div>
                              <div className="pt-3 border-t border-slate-200">
                                <p className="text-lg font-bold text-slate-900">
                                  Budget: {formatCurrency(req.budget_min)} - {formatCurrency(req.budget_max)}
                                </p>
                                <div className="flex items-center justify-between mt-2">
                                  <p className="text-sm text-slate-500 flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    {req.timeline}
                                  </p>
                                  {user && (user.role === "seller" || user.role === "admin") && (
                                    <Button size="sm" className="bg-blue-600 hover:bg-blue-700" data-testid={`respond-${req.id}`}>
                                      <MessageCircle className="w-3 h-3 mr-1" />
                                      Respond
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}

                {/* All other requirements */}
                <div>
                  {requirements.filter(r => r.featured).length > 0 && (
                    <h3 className="text-lg font-semibold text-slate-900 mb-4">All Requirements</h3>
                  )}
                  <div className="grid md:grid-cols-2 gap-6">
                    {requirements.filter(r => !r.featured).map((req) => (
                      <Card key={req.id} className="hover:shadow-lg transition-all" data-testid={`requirement-card-${req.id}`}>
                        <CardHeader>
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <CardTitle className="text-lg">{req.title}</CardTitle>
                              <CardDescription className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {req.location}
                              </CardDescription>
                            </div>
                            {getTierBadge(req.tier)}
                          </div>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-3">
                            <p className="text-sm text-slate-600 line-clamp-2">{req.description}</p>
                            <div className="flex flex-wrap gap-2">
                              <Badge variant="secondary">{req.industry}</Badge>
                              <Badge variant="secondary">{req.business_type}</Badge>
                            </div>
                            <div className="pt-3 border-t border-slate-200">
                              <p className="text-lg font-bold text-slate-900">
                                Budget: {formatCurrency(req.budget_min)} - {formatCurrency(req.budget_max)}
                              </p>
                              <div className="flex items-center justify-between mt-2">
                                <p className="text-sm text-slate-500 flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {req.timeline}
                                </p>
                                {user && (user.role === "seller" || user.role === "admin") && (
                                  <Button size="sm" className="bg-blue-600 hover:bg-blue-700" data-testid={`respond-${req.id}`}>
                                    <MessageCircle className="w-3 h-3 mr-1" />
                                    Respond
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}